import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { Search } from "@faire/web--source/slate/icons/foundation/Search";
import { FaireLogo } from "@faire/web--source/slate/logos/FaireLogo";
import { getSpacing, GrowingSpacer, Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { observer } from "mobx-react";
import { PrefetchKind } from "next/dist/client/components/router-reducer/router-reducer-types";
import React, { useEffect, useMemo } from "react";
import styled, { css } from "styled-components";
import { useNextRouterContext } from "@faire/retailer/app/_lib/routing/NextRouterContext";
import { MaybeMobileSearchBarXP } from "@faire/retailer/components/Header/mobile/MaybeMobileSearchBarXP";
import { HeaderContainer, HeaderLink } from "@faire/retailer/components/Header/styled";
import { useHeaderState } from "@faire/retailer/components/Header/useHeaderState";
import { canAddToCart } from "@faire/retailer/lib/canAddToCart";
import { useIsLoggedInRetailer } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { home } from "@faire/retailer-visitor-shared/paths/index";
import { useAssignSettingHeaderNavSignupCTACopy } from "@faire/retailer/settings/useSettingHeaderNavSignupCTACopy";
import { useAssignSettingLoggedInHomePageRouteChangePrefetching, useSettingLoggedInHomePageRouteChangePrefetching } from "@faire/retailer/settings/useSettingLoggedInHomePageRouteChangePrefetching";
import { useAssignSettingNavSearchUX, useSettingNavSearchUX } from "@faire/retailer/settings/useSettingNavSearchUX";
import { HeaderCart } from "../HeaderCart";
import { HeaderNotificationsDropdown } from "../HeaderNotifications/HeaderNotificationsDropdown";
import { HeaderSearch } from "../HeaderSearch";
import { HeaderToggleItem } from "../HeaderToggleItem";
import { HeaderMobileHamburgerMenu } from "./HeaderMobileHamburgerMenu";
import { HeaderMobileSearch } from "./HeaderMobileSearch";
import { HeaderMobileSignUp } from "./HeaderMobileSignUp";
var HeaderMobileBase = function(props) {
    var _useHeaderState = useHeaderState(), isShowingMobileSearch = _useHeaderState.isShowingMobileSearch, showMobileSearch = _useHeaderState.showMobileSearch, isShowingMenu = _useHeaderState.isShowingMenu;
    var isTablet = useViewport().isTablet;
    var isLoggedInRetailer = useIsLoggedInRetailer();
    var _useSettingNavSearchUX = useSettingNavSearchUX(), isNavSearchUX = _useSettingNavSearchUX.isTreatment;
    var assignSettingNavSearchUX = useAssignSettingNavSearchUX();
    var assignSettingHeaderNavSignupCTACopy = useAssignSettingHeaderNavSignupCTACopy();
    var _useNextRouterContext = useNextRouterContext(), nextPrefetch = _useNextRouterContext.prefetch;
    useEffect(function() {
        assignSettingNavSearchUX();
    }, [
        assignSettingNavSearchUX
    ]);
    useEffect(function() {
        assignSettingHeaderNavSignupCTACopy();
    }, [
        assignSettingHeaderNavSignupCTACopy
    ]);
    var isLoggedInHomePageRouteChangePrefetching = useSettingLoggedInHomePageRouteChangePrefetching();
    var assignSettingLoggedInHomePageRouteChangePrefetching = useAssignSettingLoggedInHomePageRouteChangePrefetching();
    var onLogoHover = useMemo(function() {
        return function() {
            nextPrefetch(home(), {
                kind: isLoggedInHomePageRouteChangePrefetching ? PrefetchKind.FULL : PrefetchKind.AUTO
            });
        };
    }, [
        nextPrefetch,
        isLoggedInHomePageRouteChangePrefetching
    ]);
    var onLogoClick = useMemo(function() {
        return function() {
            return assignSettingLoggedInHomePageRouteChangePrefetching();
        };
    }, [
        assignSettingLoggedInHomePageRouteChangePrefetching
    ]);
    var renderHeaderItems = function() {
        var mobileSearch = /*#__PURE__*/ _jsx(HeaderMobileSearch, {
            inputId: "mobile-top-search",
            inputDataTestId: "mobileSearchBarInput"
        });
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                isLoggedInRetailer ? null : /*#__PURE__*/ _jsx(TabletFixedSizeSpacer, {
                    width: 124
                }),
                isTablet ? /*#__PURE__*/ _jsx(TabletHeaderSearch, {
                    inputId: "tablet-top-search",
                    inputDataTestId: "tabletSearchBarInput"
                }) : null,
                isLoggedInRetailer ? /*#__PURE__*/ _jsx(MobileOnlyGrowingSpacer, {}) : /*#__PURE__*/ _jsx(GrowingSpacer, {}),
                !isLoggedInRetailer && !isNavSearchUX ? /*#__PURE__*/ _jsx(MobileHeaderToggleItem, {
                    showToggleChild: isShowingMobileSearch,
                    toggleChild: mobileSearch,
                    onToggle: showMobileSearch,
                    id: "search",
                    children: /*#__PURE__*/ _jsx(Search, {
                        fontSize: "16px",
                        strokeWidth: 2,
                        color: Core.icon.default
                    })
                }) : null,
                /*#__PURE__*/ _jsx(HeaderNotificationsDropdown, {}),
                /*#__PURE__*/ _jsx(HeaderMobileSignUp, {}),
                canAddToCart() ? /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(StyledHeaderCart, {
                            dataTestId: "mobileHeaderCartButton"
                        }),
                        isLoggedInRetailer ? /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(MobileOnlySpacer, {
                                    width: "2x"
                                }),
                                /*#__PURE__*/ _jsx(TabletOnlySpacer, {
                                    width: 13
                                })
                            ]
                        }) : /*#__PURE__*/ _jsx(Spacer, {
                            width: "2x"
                        })
                    ]
                }) : null
            ]
        });
    };
    return /*#__PURE__*/ _jsxs(StyledHeaderContainer, {
        className: props.className,
        children: [
            /*#__PURE__*/ _jsxs(Wrapper, {
                children: [
                    isLoggedInRetailer ? /*#__PURE__*/ _jsx(TabletOnlySpacer, {
                        width: "1x"
                    }) : null,
                    /*#__PURE__*/ _jsx(HeaderMobileHamburgerMenu, {}),
                    isLoggedInRetailer ? /*#__PURE__*/ _jsx(Spacer, {
                        width: "2x"
                    }) : null,
                    /*#__PURE__*/ _jsx(HeaderLogoLink, {
                        id: "faire-logo-link",
                        to: home(),
                        $marginTop: isLoggedInRetailer,
                        onMouseEnter: onLogoHover,
                        onTouchStart: onLogoHover,
                        onClick: onLogoClick,
                        $absolutePositioning: !isLoggedInRetailer,
                        children: /*#__PURE__*/ _jsx(RepsonsiveFaireLogo, {
                            mobileSmall: true,
                            inverse: isShowingMenu
                        })
                    }),
                    isLoggedInRetailer ? /*#__PURE__*/ _jsx(TabletOnlySpacer, {
                        width: 13
                    }) : null,
                    renderHeaderItems()
                ]
            }),
            isLoggedInRetailer ? /*#__PURE__*/ _jsx(Wrapper, {
                children: /*#__PURE__*/ _jsx(MaybeMobileSearchBarXP, {})
            }) : null
        ]
    });
};
export var HeaderMobile = observer(HeaderMobileBase);
var RepsonsiveFaireLogo = styled(FaireLogo).withConfig({
    componentId: "sc-539c8f93-0"
})([
    "",
    "{width:104px;}"
], responsiveMediaQueries.tabletAndBelow);
var StyledHeaderCart = styled(HeaderCart).withConfig({
    componentId: "sc-539c8f93-1"
})([
    "svg{color:",
    ";}"
], Core.text.default);
var StyledHeaderContainer = styled(HeaderContainer).withConfig({
    componentId: "sc-539c8f93-2"
})([
    "border-color:",
    ";background:",
    ";"
], Core.border.muted, Core.surface.inverse);
var Wrapper = styled.div.withConfig({
    componentId: "sc-539c8f93-3"
})([
    "display:flex;flex-direction:row;align-items:center;position:relative;background-color:",
    ";"
], Core.surface.inverse);
var HeaderLogoLink = styled(HeaderLink).withConfig({
    componentId: "sc-539c8f93-4"
})([
    "padding-left:0;",
    ";padding-right:0;transition:left 0.1s linear;",
    "{",
    "}"
], function(props) {
    return props.$absolutePositioning ? css([
        "top:0;position:absolute;@media (min-width:0px) and (max-width:320px){left:44px;}@media (min-width:321px) and (max-width:390px){left:48px;}@media (min-width:391px){left:54px;}"
    ]) : "";
}, responsiveMediaQueries.mobile, function(props) {
    return props.$marginTop ? css([
        "margin-top:",
        ";"
    ], getSpacing("2x")) : null;
});
var MobileHeaderToggleItem = styled(HeaderToggleItem).withConfig({
    componentId: "sc-539c8f93-5"
})([
    "",
    "{display:none;}"
], responsiveMediaQueries.tabletAndAbove);
var TabletHeaderSearch = styled(HeaderSearch).withConfig({
    componentId: "sc-539c8f93-6"
})([
    "display:none;",
    "{display:inherit;}"
], responsiveMediaQueries.tabletAndAbove);
var TabletFixedSizeSpacer = styled(Spacer).withConfig({
    componentId: "sc-539c8f93-7"
})([
    "display:none;",
    "{display:inherit;}"
], responsiveMediaQueries.tabletAndAbove);
var MobileOnlySpacer = styled(Spacer).withConfig({
    componentId: "sc-539c8f93-8"
})([
    "",
    "{display:none;}"
], responsiveMediaQueries.tabletAndAbove);
var TabletOnlySpacer = styled(Spacer).withConfig({
    componentId: "sc-539c8f93-9"
})([
    "",
    "{display:none;}"
], responsiveMediaQueries.mobile);
var MobileOnlyGrowingSpacer = styled(GrowingSpacer).withConfig({
    componentId: "sc-539c8f93-10"
})([
    "",
    "{display:none;}"
], responsiveMediaQueries.tabletAndAbove);
